import { api } from './api-client';
import type { Paginated } from './tenants-api';

// Contrats de platform-api (chantier 9.6a) : apps/platform-api/src/routes/admins.ts

export const ADMIN_STATUSES = ['ACTIVE', 'INVITED', 'SUSPENDED'] as const;
export type AdminStatus = (typeof ADMIN_STATUSES)[number];

export interface AdminListItem {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  status: AdminStatus;
  lastLoginAt: string | null;
  createdAt: string;
  mfaBackupCodesCount: number;
}

export interface ListAdminsParams {
  page: number;
  pageSize: number;
  status?: AdminStatus;
  search?: string;
}

export interface AdminDetail extends AdminListItem {
  mfaEnabled: boolean;
  mustChangePassword: boolean;
  updatedAt: string;
  /** Lignes d'audit dont l'admin est l'auteur (connexions et échecs compris), sur 7 et 30 jours glissants. */
  activity: { last7Days: number; last30Days: number };
}

/** Réponse de suspend / reactivate : état de l'admin après changement de statut. */
export type AdminState = Pick<AdminDetail, 'id' | 'email' | 'firstName' | 'lastName' | 'status' | 'mfaEnabled' | 'updatedAt'>;

export interface CreateAdminInput {
  email: string;
  firstName: string;
  lastName: string;
}

/** Secrets MFA en clair, renvoyés une seule fois (création et reset MFA). mfaQrCode = URI otpauth://. */
export interface MfaCredentials {
  mfaSecret: string;
  mfaQrCode: string;
  backupCodes: string[];
}

export interface CreateAdminResponse {
  admin: Pick<AdminDetail, 'id' | 'email' | 'firstName' | 'lastName' | 'status'>;
  initialCredentials: MfaCredentials & { password: string };
}

export const adminKeys = {
  all: ['platform', 'admins'] as const,
  list: (params: ListAdminsParams) => [...adminKeys.all, 'list', params] as const,
  detail: (id: string) => [...adminKeys.all, 'detail', id] as const,
};

const path = (id: string) => `/admins/${encodeURIComponent(id)}`;

export const adminsApi = {
  list: async (params: ListAdminsParams): Promise<Paginated<AdminListItem>> =>
    (await api.get<Paginated<AdminListItem>>('/admins', { params })).data,
  get: async (id: string): Promise<AdminDetail> => (await api.get<AdminDetail>(path(id))).data,
  create: async (input: CreateAdminInput): Promise<CreateAdminResponse> =>
    (await api.post<CreateAdminResponse>('/admins', input)).data,
  suspend: async (id: string, reason: string): Promise<AdminState> =>
    (await api.patch<AdminState>(`${path(id)}/suspend`, { reason })).data,
  reactivate: async (id: string): Promise<AdminState> => (await api.patch<AdminState>(`${path(id)}/reactivate`)).data,
  resetMfa: async (id: string): Promise<MfaCredentials> => (await api.post<MfaCredentials>(`${path(id)}/reset-mfa`)).data,
  remove: async (id: string): Promise<void> => {
    await api.delete(path(id));
  },
};
